import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { LoaderComponent } from '../../features/comman/loader/loader';
import { APP_CONFIG } from '../constants/app.constants';
import { FEDERAL_HOLIDAY_SET } from '../constants/holidays';

@Injectable({
  providedIn: 'root'
})
export class CommonService {
  status = APP_CONFIG.ALL_STATUS;
  private loaderDialogRef?: MatDialogRef<LoaderComponent>;

  constructor(private dialog: MatDialog, private snack: MatSnackBar) { }

  showLoader(): void {
    if (!this.loaderDialogRef) {
      this.loaderDialogRef = this.dialog.open(LoaderComponent, {
        disableClose: true,
        panelClass: 'loader-dialog',
      });
    }
  }

  showConsole(msg: any) {
    console.log(msg)
  }

  hideLoader(): void {
    this.loaderDialogRef?.close();
    this.loaderDialogRef = undefined;
  }

  success(message: string) {
    this.snack.open(message, 'OK', {
      duration: 3000,
      panelClass: ['snack-success'],
      horizontalPosition: 'center',
      verticalPosition: 'bottom'
    });
  }

  error(message: string) {
    this.snack.open(message, 'Close', {
      duration: 5000,
      panelClass: ['snack-error'],
      horizontalPosition: 'center',
      verticalPosition: 'bottom'
    });
  }

  info(message: string) {
    this.snack.open(message, 'OK', {
      duration: 4000,
      panelClass: ['snack-info'],
      horizontalPosition: 'center',
      verticalPosition: 'bottom'
    });
  }

  warning(message: string) {
    this.snack.open(message, 'OK', {
      duration: 5000,
      panelClass: ['snack-warning'],
      horizontalPosition: 'center',
      verticalPosition: 'bottom'
    });
  }

  timeDisplay(record: any, action: any) {
    let actionDate = this.getEffectiveDealDate(record);
    const now = new Date();
    const elapsedHours = (now.getTime() - actionDate.getTime()) / (1000 * 60 * 60);
    let remainingHours = Math.floor(Math.max(0, 48 - elapsedHours));
    if (remainingHours > 30 && remainingHours <= 48) {
      remainingHours = Math.floor(30 - elapsedHours)
    }
    if (elapsedHours >= 48) {
      return "Overdue"
    } else if (remainingHours == 0) {
      const minutes = Math.max(1, Math.ceil((48 - elapsedHours) * 60))
      return "Due in " + minutes + " Minutes"
    } else if (action == "remaining" && remainingHours > 0) {
      return "+" + remainingHours + " hrs remaining"
    } else if (action == "due") {
      return remainingHours > 0 ? "Due in " + remainingHours + " Hrs" : "Overdue"
    } else {
      return "N/A"
    }
  }

  getStreak(todo: any) {
    const start = this.getEffectiveDealDate(todo);
    const now = new Date()
    //const now = new Date('2026-08-05T11:00:55+05:30')
    let elapsedHours = Math.floor((now.getTime() - start.getTime()) / (1000 * 60 * 60));
    const elapsedMinutes = this.getWorkingMinutes(start, now);
    const GOLD = 120;
    const SILVER = 240;
    const BRONZE = 360;

    let leftFor = '';
    let leftMedal = '';
    let duration = '';
    let remaining = 0;

    let dealClass = 'green-card'
    if (elapsedHours < 30) {
      dealClass = 'green-card'
    } else if (elapsedHours > 30 && elapsedHours < 48) {
      dealClass = 'yellow-card'
    } else {
      dealClass = 'red-card'
    }
    if (elapsedMinutes < GOLD) {
      leftFor = 'Left for GOLD';
      leftMedal = 'gold';
      duration = '0–2 hrs · Pulsing';
      remaining = GOLD - elapsedMinutes;
    } else if (elapsedMinutes < SILVER) {
      leftFor = 'Left for SILVER';
      leftMedal = 'silver';
      duration = '2–4 hrs · Pulsing';
      remaining = SILVER - elapsedMinutes;
    } else if (elapsedMinutes < BRONZE) {
      leftFor = 'Left for BRONZE';
      leftMedal = 'bronze';
      duration = '4–6 hrs · Pulsing';
      remaining = BRONZE - elapsedMinutes;
    } else {
      leftFor = 'Time Over!!';
      leftMedal = 'expired';
      duration = '6+ hrs';
      remaining = 0;
    }
    duration = '' //hide it, as per client req.

    return {
      hours: Math.floor(remaining / 60),
      minutes: remaining % 60,
      leftFor,
      leftMedal,
      duration,
      dealClass
    };
  }

  /**
  * The deal date the clocks should actually run from.
  *
  * A New referral that lands outside working hours — a weekend, a federal
  * holiday, or before 9am / after 9pm on a working day — gives the coordinator
  * no chance to act, so its clock only starts at 9am on the next working day.
  * A Friday 10pm deal therefore counts from Monday 9am, and if that Monday is
  * a federal holiday it rolls on to Tuesday 9am.
  *
  * Every other status is left alone: the raw dealDate is returned unchanged.
  */
  getEffectiveDealDate(record: any): Date {
    const dealDate = new Date(record?.dealDate);
    if (record?.status !== this.status.NEW || isNaN(dealDate.getTime())) {
      return dealDate;
    }

    const WORK_START = APP_CONFIG.START_WORKING_TIME;
    const WORK_END = APP_CONFIG.END_WORKING_TIME;

    // Within a working day's hours — the clock already starts when it should.
    if (this.isWorkingDay(dealDate) && dealDate.getHours() >= WORK_START && dealDate.getHours() < WORK_END) {
      return dealDate;
    }

    const effective = new Date(dealDate);
    // Before 9am on a working day only needs the time moved forward; anything
    // else (weekend, holiday, past 9pm) starts looking from the following day.
    if (!(this.isWorkingDay(effective) && effective.getHours() < WORK_START)) {
      effective.setDate(effective.getDate() + 1);
    }
    effective.setHours(WORK_START, 0, 0, 0);

    // Skip whole weekends and holidays until a working day is reached.
    while (!this.isWorkingDay(effective)) {
      effective.setDate(effective.getDate() + 1);
    }

    return effective;
  }

  /** A weekday that is not a US federal holiday. */
  isWorkingDay(date: Date): boolean {
    const day = date.getDay();
    if (day === 0 || day === 6) return false;

    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    return !FEDERAL_HOLIDAY_SET.has(key);
  }

  getWorkingMinutes(start: Date, end: Date): number {
    const WORK_START = APP_CONFIG.START_WORKING_TIME
    const WORK_END = APP_CONFIG.END_WORKING_TIME
    let totalMinutes = 0;
    let current = new Date(start);

    while (current < end) {
      const workStart = new Date(current);
      workStart.setHours(WORK_START, 0, 0, 0);

      const workEnd = new Date(current);
      workEnd.setHours(WORK_END, 0, 0, 0);

      // Before working hours
      if (current < workStart) {
        current = new Date(workStart);
      }

      // After working hours
      if (current >= workEnd) {
        current.setDate(current.getDate() + 1);
        current.setHours(WORK_START, 0, 0, 0);
        continue;
      }

      const intervalEnd = end < workEnd ? end : workEnd;
      totalMinutes += (intervalEnd.getTime() - current.getTime()) / 60000;
      current = intervalEnd;
      if (current >= workEnd) {
        current.setDate(current.getDate() + 1);
        current.setHours(WORK_START, 0, 0, 0);
      }
    }

    return Math.max(0, Math.floor(totalMinutes));
  }

  getAge(dob: string | Date): any {
    if (!dob) return "-"

    const birthDate = new Date(dob);
    const today = new Date();

    let age = today.getFullYear() - birthDate.getFullYear();

    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }
    return age
  }

  getTimeAgo(date: string | Date): string {
    const givenDate = new Date(date);
    const now = new Date();

    const seconds = Math.floor((now.getTime() - givenDate.getTime()) / 1000);

    if (seconds < 60) {
      return `${seconds} second${seconds !== 1 ? 's' : ''} ago`;
    }

    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) {
      return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
    }

    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
      return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    }

    const days = Math.floor(hours / 24);
    if (days < 7) {
      return `${days} day${days !== 1 ? 's' : ''} ago`;
    }

    const weeks = Math.floor(days / 7);
    if (weeks < 4) {
      return `${weeks} week${weeks !== 1 ? 's' : ''} ago`;
    }

    const months = Math.floor(days / 30);
    if (months < 12) {
      return `${months} month${months !== 1 ? 's' : ''} ago`;
    }

    const years = Math.floor(days / 365);
    return `${years} year${years !== 1 ? 's' : ''} ago`;
  }

  isValidPhone(phone: string): boolean {
    return /^[0-9]{10}$/.test(phone);
  }

  isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  /**
  * Which of the four All Referrals priority buckets a deal sits in, decided
  * by its status — Priority 1 (an untouched New referral) is the most urgent.
  * Mirrors PRIORITY_*_STATUS on the server, which counts the same buckets for
  * the Due Today and Overdue tiles.
  *
  * Returns null for anything outside the buckets (closed-out referrals), so
  * the badge hides rather than showing a meaningless priority.
  */
  priorityLabel(deal: any): string | null {
    const status = deal?.status;
    if (!status) return null;

    const buckets: string[][] = [
      [this.status.NEW],
      [this.status.SCHEDULED_FOR_PRESCREEN, this.status.SCHEDULED_FOR_ONSITE_SCREENING_ICF],
      [
        this.status.NO_SHOW_FOR_PRESCREEN,
        this.status.NO_SHOW_FOR_SCREENING,
        this.status.FIRST_ATTEMPT_NO_CONTACT,
        this.status.SECOND_ATTEMPT_NO_CONTACT,
        this.status.THIRD_ATTEMPT_NO_CONTACT
      ],
      [
        this.status.ON_HOLD_PENDING_MEDICAL_RECORD,
        this.status.ON_HOLD_PATIENT_REASON,
        this.status.INFORMED_CONSENT_SIGNED
      ]
    ];

    const index = buckets.findIndex(statuses => statuses.includes(status));
    return index === -1 ? null : `Priority ${index + 1}`;
  }

  isToday(updateDealDate: string | Date): boolean {
    const updateDate = new Date(updateDealDate);
    const today = new Date();

    return (
      updateDate.getFullYear() === today.getFullYear() &&
      updateDate.getMonth() === today.getMonth() &&
      updateDate.getDate() === today.getDate()
    );
  }
}
